import React, { useEffect, useState } from 'react';
import { Redirect, Route } from 'react-router-dom';
import { IonApp, IonRouterOutlet, IonToast, setupIonicReact } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import Questionnaire from './pages/StarterQuestions';
import Home from './pages/Home/Home';
import type {} from '@mui/x-date-pickers/themeAugmentation';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/* Theme variables */
import './theme/variables.css';

// Swipper styling
import 'swiper/css';

// General app styling
import './style.css';

import Login from './pages/Auth/Login';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/fr-ch';
import { createTheme, ThemeProvider } from '@mui/material';
import { MainProvider, useMain } from './store/StoredContext';
import { LocationProvider } from './store/LocationContext';
import ErrorBoundary from './components/Errors/ErrorBoundary';
import { EphemeralProvider } from './store/EphemeralContext';

const theme = createTheme({
  components: {
    MuiClock: {
      styleOverrides: {
        root: {
          '& .MuiClock-pin': {
            backgroundColor: '#11D39C' // Couleur de l'aiguille
          },
          '& .MuiClockPointer-root': {
            backgroundColor: '#11D39C' // Couleur de la partie longue de l'aiguille
          },
          '& .MuiClockPointer-thumb': {
            border: '16px solid #11D39C', // Couleur du bout de l'aiguille
            backgroundColor: '#11D39C'
          }
        }
      }
    },
    MuiPickersToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: '#11D39C' // Couleur du fond du toolbar en haut
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          '&.MuiButtonBase-root': {
            color: '#11D39C' // Couleur des libellés en bas
          }
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&.MuiMultiSectionDigitalClockSection-item.Mui-selected': {
            backgroundColor: '#11D39C'
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '0.75rem',
          borderColor: '#11D39C',
          height: '42px',
          marginRight: '10px',
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#11D39C' // Couleur du contour en focus
          },
          '& .MuiInputBase-input': {
            textAlign: 'center', // Centrer horizontalement
            padding: '14px 0' // Ajuster le padding pour le centrage vertical
          }
        }
      }
    }
  }
});

setupIonicReact();

function requireClient(Component: React.ComponentType) {
  return function ProtectedComponent(props: any) {
    const {
      state: { token }
    } = useMain();

    if (
      token == null ||
      token === undefined
      //  || userRole !== 'client'
    ) {
      return <Redirect to="/login" />;
    }

    return <Component {...props} />;
  };
}

const App: React.FC = () => {
  const [isUpdated, setUpdated] = useState(false);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      void navigator.serviceWorker.getRegistration().then((registration) => {
        if ((registration?.waiting) != null) {
          // Envoie le message pour activer immédiatement
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
      });

      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (navigator.serviceWorker.controller != null) {
          setUpdated(true);
        }
      });
    }
  }, []);

  const ClientQuestionnaire = requireClient(Questionnaire);

  return (
    <LocationProvider>
      <MainProvider>
        <EphemeralProvider>
          <ThemeProvider theme={theme}>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale="fr-ch"
              localeText={{
                cancelButtonLabel: 'Annuler'
              }}
            >
              <IonApp>
                <IonReactRouter>
                  <ErrorBoundary>
                    <IonRouterOutlet>
                      <Route path="/login" component={Login} exact />
                      <Route path="/home" component={Home} exact />
                      <Route path="/questions" component={ClientQuestionnaire} exact />
                      <Route exact path="/">
                        <Redirect to="/home" />
                      </Route>
                    </IonRouterOutlet>
                    <IonToast
                      isOpen={isUpdated}
                      message="Une nouvelle version de l'application est disponible, relancez pour y accéder"
                      duration={5000}
                    ></IonToast>
                  </ErrorBoundary>
                </IonReactRouter>
              </IonApp>
            </LocalizationProvider>
          </ThemeProvider>
        </EphemeralProvider>
      </MainProvider>
    </LocationProvider>
  );
};

export default App;
